<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import { FormLabel, FormInput } from "../../../../base-components/Form";
import Button from "../../../../base-components/Button";
import Table from "../../../../base-components/Table";
import { computed, onBeforeMount, Ref, ref } from "vue";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import { usePedidosStore } from "../../../../services/pedidos";
import { numeroFormateado } from "../../../../utils/helper";
import { Pedido, PedidoItem } from "@fixeduy/restaurantestypes";
import { utilFechaConvertirNumeroString } from "../../../../utils/fechaFixedTypes";
import { userUserStore } from "../../../../stores/user";
import { useUsuariosStore } from "../../../../services/usuarios";
import { useMesasStore } from "../../../../services/mesas";
import { useSectoresStore } from "../../../../services/sectores";

interface Props {
  pedido: Pedido;
}

const props = defineProps<Props>();
const sendButtonRef = ref(null);
const cargando = ref(false);
const mesasStore = useMesasStore();
const sectoresStore = useSectoresStore();
const identificador = computed(() => {
  if (!props.pedido) return "";
  const sectorId = mesasStore.getById(props.pedido.mesaId!)?.sectorId ?? "";
  const sectorNombre =
    sectoresStore.sectores.find(data => data.id == sectorId)?.nombre ?? "";
  return `${sectorNombre}: ${props.pedido.mesaNombre}  ${utilFechaConvertirNumeroString(props.pedido.fechaCreacion, true, "/")}`;
});
const userStore = userUserStore();
const usuariosStore = useUsuariosStore();
const pedidosStore = usePedidosStore();
const pedidosLineas: Ref<PedidoItem[]> = ref([]);

onBeforeMount(async () => {
  const lineas = await pedidosStore.getPedidoById(props.pedido.id);

  pedidosLineas.value = lineas.sort(
    (a, b) => b.fechaCreacion - a.fechaCreacion
  );
});

const emit = defineEmits<{
  (e: "cerrar"): void;
}>();
</script>

<template>
  <Dialog
    :open="true"
    @close="
      () => {
        emit('cerrar');
      }
    "
    :initialFocus="sendButtonRef"
    size="xxl"
  >
    <Dialog.Panel
      class="overflow-auto flex flex-col max-w-[100vw] max-h-[85vh]"
    >
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">
          Información ampliada pedido # {{ props.pedido.numero }}
        </h2>
      </Dialog.Title>
      <!-------------------- DIALOG DESCRIPTION ------------------------>

      <Dialog.Description>
        <!-------------------- Nombre ------------------------>
        <div class="flex">
          <div class="flex-none w-50 pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1 ml-1"> Pedido</FormLabel>
          </div>
          <div class="flex-1 ml-2">
            <FormInput type="text" disabled :value="identificador" />
          </div>
        </div>
        <!-------------------- Usuario creador ------------------------>
        <div class="flex mt-2">
          <div class="flex-none w-50 pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1 ml-1"> Usuario </FormLabel>
          </div>
          <div class="flex-1 ml-2">
            <FormInput
              type="text"
              disabled
              :value="usuariosStore.getById(props.pedido.usuario)?.nombre ?? ''"
            />
          </div>
        </div>
        <div class="flex mt-2">
          <div class="flex-none w-50 pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1 ml-1"> Creada: </FormLabel>
          </div>
          <div class="flex-1 ml-2">
            <FormInput type="text" disabled :value="props.pedido.creadaDesde" />
          </div>
        </div>
      </Dialog.Description>
      <div class="m-6">
        <Table bordered striped>
          <Table.Thead>
            <Table.Tr v-if="cargando && pedidosLineas.length > 20">
              <Table.Td>
                <div class="flex flex-col items-center">
                  <div class="w-8">
                    <LoadingIcon icon="oval" class="w-8 h-8" />
                  </div>
                </div>
                <p class="mt-2 mb-8 text-center">Cargando...</p>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th class="whitespace-nowrap"> Estado</Table.Th>
              <Table.Th class="whitespace-nowrap"> Cantidad</Table.Th>
              <Table.Th class="whitespace-nowrap"> Hora</Table.Th>
              <Table.Th class="whitespace-nowrap">
                Detalle (cant. | descripción | importe)</Table.Th
              >
              <Table.Th class="whitespace-nowrap text-right">
                P. Unitario s/descuento</Table.Th
              >
              <Table.Th
                class="whitespace-nowrap text-right"
                v-if="props.pedido.descuentoMonto != 0"
              >
                Descuento</Table.Th
              >
              <Table.Th class="whitespace-nowrap text-right"> Total</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <template v-for="linea in pedidosLineas" :key="linea.pedidoId">
              <Table.Tr>
                <Table.Td>{{ linea.estado }} </Table.Td>
                <Table.Td>{{ linea.cantidad }} </Table.Td>
                <Table.Td
                  >{{
                    utilFechaConvertirNumeroString(
                      linea.fechaCreacion,
                      true,
                      "/"
                    )
                  }}
                </Table.Td>
                <Table.Td>{{ linea.nombre }} </Table.Td>
                <Table.Td class="text-right"
                  >{{ numeroFormateado(linea.precioUnitarioConIva) }} </Table.Td
                ><Table.Td
                  class="text-right"
                  v-if="props.pedido.descuentoMonto != 0"
                  >{{ numeroFormateado(linea.descuentoMonto) }}
                </Table.Td>
                <Table.Td class="text-right"
                  >{{ numeroFormateado(linea.total) }}
                </Table.Td>
              </Table.Tr>
            </template>
            <Table.Tr v-if="props.pedido.descuentoMonto != 0">
              <Table.Td colspan="6" class="text-right font-bold">
                Descuentos
              </Table.Td>
              <Table.Td colspan="1" class="text-right">
                {{ props.pedido.descuentoMonto }}</Table.Td
              >
            </Table.Tr>
            <!-- TOTAL -->
            <Table.Tr
              class="font-semibold bg-slate-100/50 dark:bg-darkmode-600/40"
            >
              <Table.Td
                :colspan="props.pedido.descuentoMonto > 0 ? 6 : 5"
                class="text-right"
              >
                Total pedido
              </Table.Td>
              <Table.Td colspan="1" class="text-right">
                {{ numeroFormateado(props.pedido.total) }}
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </div>

      <!-------------------- DIALOG FOOTER ------------------------>
      <Dialog.Footer>
        <div v-if="cargando">
          <div
            class="flex flex-col items-rigth justify-end col-span-6 sm:col-span-3 xl:col-span-2"
          >
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
        <div v-else>
          <Button
            variant="secondary"
            class="mb-2 mr-3"
            @click="
              () => {
                emit('cerrar');
              }
            "
          >
            Cerrar</Button
          >
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
